import React, { memo, useCallback, useMemo, useRef } from 'react';
import { FlatList, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import Animated, { interpolate, useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { LiveIcon } from '@/assets/icons/live-indicator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { common } from '@/styles/styles';
import { EventStatus } from '@/types/api.generated';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { formatPlayerName } from '@/utils/format-player-name';
import { getPlayerPositionOrRank } from '@/utils/formatPlayerInfo';

import { AnalyticsEvent } from '../analytics/constants';
import { PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { createPlayerEventKey } from '../betslip-pickem/utils/markets-cache-store-utils';
import { useFullScreenPagerTransition } from '../circular-pager/FullScreenPagerTransitionProvider';
import { useScaleCarouselItemMountAnimation } from '../circular-pager/hooks/use-scale-carousel-item-mount-animation';
import { useStartAutoScroll } from '../circular-pager/hooks/use-start-auto-scroll';
import {
    AUTO_SCROLL_LIST_ENABLED,
    AutoScrollingListLoop,
    AutoScrollingListLoopRef,
} from '../fantasy-event-sheets/auto-scrolling-list';
import { usePlayerProfileDataStore } from './hooks/use-player-profile-data-store';

const AUTO_SCROLL_LIST_SPACING = 12;

export const PlayersCarouselTabBar = memo(() => {
    const { animatedIndex, sheetMountAnimationProgress, isSheetMountCompleted } = useFullScreenPagerTransition();

    const mountingStyles = useAnimatedStyle(() => {
        return {
            opacity: interpolate(sheetMountAnimationProgress.value, [-1, -0.5, 0], [0, 1, 1]),
        };
    });

    const bottomSheetGestureStyles = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {};
        }

        return {
            opacity: interpolate(animatedIndex.value, [-1, 0, 1], [0, 1, 0]),
        };
    });

    const zIndexStyles = useAnimatedStyle(() => {
        return {
            zIndex: animatedIndex.value > 0 ? -1 : 1,
        };
    });

    const position = isWeb ? undefined : common.absolute;

    return (
        <Animated.View style={[position, zIndexStyles, mountingStyles, bottomSheetGestureStyles]}>
            <PlayersCarouselTabBarContents />
        </Animated.View>
    );
});

export const PlayersCarouselTabBarContents = () => {
    const insets = useSafeAreaInsets();
    const { width } = useWindowDimensions();
    const listOfPlayers = usePlayerProfileDataStore(state => state.playersList);

    const autoScrollingListRef = useRef<AutoScrollingListLoopRef>(null);

    useStartAutoScroll(autoScrollingListRef, AUTO_SCROLL_LIST_ENABLED);

    // we need this only for the initial render of the carousel
    // so we use useMemo with an empty array to avoid re-computing it on every render
    const initialIndex = useMemo(() => usePlayerProfileDataStore.getState().selectedPlayerIndex, []);

    return (
        <Box
            style={{ marginTop: insets.top }}
            paddingTop={'s12'}
            zIndex={1}
            width={isWeb ? 'auto' : width}
            overflow={'hidden'}
        >
            {AUTO_SCROLL_LIST_ENABLED ? (
                <AutoScrollingListLoop
                    spacing={AUTO_SCROLL_LIST_SPACING}
                    frameRate={60}
                    style={common.justifyCenter}
                    speed={0.2}
                    autostart={false}
                    ref={autoScrollingListRef}
                    data={listOfPlayers}
                    renderItem={({ item, index }) => <PlayerCarouselItem player={item} index={index} />}
                    initialIndex={initialIndex}
                />
            ) : (
                <FlatList
                    contentContainerStyle={styles.list}
                    data={listOfPlayers}
                    horizontal
                    keyExtractor={item => createPlayerEventKey(item.id, item.event.id)}
                    renderItem={({ item, index }) => <PlayerCarouselItem player={item} index={index} />}
                    showsHorizontalScrollIndicator={showScrollIndicator}
                />
            )}
        </Box>
    );
};

const PlayerCarouselItem = ({ player, index }: { player: PlayerWithTeamAndEvent; index: number }) => {
    const formattedName = formatPlayerName(player, false);
    const playerPosition = getPlayerPositionOrRank(player, player?.event);

    const isSelected = usePlayerProfileDataStore(state => state.selectedPlayerIndex === index);

    const isLive = player?.event?.status === EventStatus.InProgress;

    const onPress = useCallback(() => {
        usePlayerProfileDataStore.getState().actions.setSelectedPlayerIndex(index);
        BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PAGER_PLAYER_CLICK);
    }, [index]);

    const animatedStyles = useScaleCarouselItemMountAnimation();

    return (
        <Animated.View style={animatedStyles}>
            <TouchableOpacity onPress={onPress}>
                <Row
                    borderRadius={'r12'}
                    borderWidth={1}
                    style={[isSelected ? styles.selectedContainer : styles.container, common.borderCurve]}
                    paddingHorizontal={'s12'}
                    paddingVertical={'s10'}
                    alignItems={'center'}
                >
                    {isLive ? (
                        <Box mr={'s4'}>
                            <LiveIcon size={12} />
                        </Box>
                    ) : null}
                    <Text numberOfLines={1}>
                        <Text
                            variant={'titleSmall'}
                            color={isSelected ? ds.colorToken.text.primary : 'neutral300'}
                            numberOfLines={1}
                        >
                            {formattedName}
                        </Text>
                        <Text variant={'bodySmall'} color={isSelected ? ds.colorToken.text.secondary : 'neutral300'}>
                            {playerPosition ? ` • ${playerPosition}` : ''}
                        </Text>
                    </Text>
                </Row>
            </TouchableOpacity>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        borderColor: ds.colorValue.border.subtle,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    selectedContainer: {
        borderColor: ds.colorValue.border.strong,
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    list: {
        gap: AUTO_SCROLL_LIST_SPACING,
        justifyContent: 'center',
        flexGrow: 1,
    },
});
